import React, { useState } from 'react'
import '../styles/ScholarLogout.css'
import '../styles/SharedLayout.css'
import '../styles/MobileMenuShared.css'
import { Layout, Button, Space, Typography, Card} from 'antd'
import { LogoutOutlined, MenuOutlined, FileTextOutlined, UserOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { postRequest } from '../utilities/apicalls'
import { clearUserSession } from '../utilities/commons'
import ScholarSidebar from '../components/ScholarSidebar'
import ToastMessage from '../components/ToastMessage'

const { Content } = Layout
const { Title, Text } = Typography

/**
 * ScholarLogout Component
 * 
 * Displays the logout confirmation page with navigation sidebar
 * @returns {JSX.Element} The ScholarLogout component
 */
const ScholarLogout = () => {
	const history = useHistory();
	
	// State for mobile sidebar visibility
    const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
    
    // Toggle mobile sidebar
    const toggleMobileSidebarVisibility = () => {
        setMobileSidebarVisible(!mobileSidebarVisible);
    };
    
    // Handle mobile menu item clicks
    const handleMobileMenuClick = (path) => {
        setMobileSidebarVisible(false);
        history.push(path);
    };
    
    // Mobile menu items
    const mobileMenuItems = [
        {
            key: 'acknowledgement-receipt',
            icon: <FileTextOutlined />,
            label: 'AR',
            onClick: () => handleMobileMenuClick('/scholar-acknowledgement-receipt')
        },
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profile',
            onClick: () => handleMobileMenuClick('/scholar-profile')
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            onClick: () => handleMobileMenuClick('/scholar-logout')
        }
    ];

	// Event handlers
	const handleCancel = () => {
		history.push('/scholar-acknowledgement-form');
	};

	// Logout handler with API connection
	const handleLogout = async () => {
		try {
			const token = localStorage.getItem('token');
			const scholarId = localStorage.getItem('scholar_id');

			if (!scholarId || !token) {
				// console.error('Scholar ID or token is missing');
				ToastMessage.error('Unable to logout: Missing scholar information');
				return;
			}

			// Create a FormData object for the API request
			const formData = new FormData();
			formData.append('token', token);
			formData.append('requester', scholarId);
			
			// API call to logout the user using FormData
			const response = await postRequest('scholars/logout', formData, true, true); // true, true for FormData
			
			// console.log('API Response:', response);

			if (response.status === 200) {
				const cleared = clearUserSession();
				if (cleared) {
					ToastMessage.success('Logged out successfully');
					setTimeout(() => {
						history.push('/scholar-login');
					}, 1000);
					localStorage.removeItem('sidebar_collapsed');
					localStorage.removeItem('admin_sidebar_collapsed');
				} else {
					// console.warn('Session clear warning: Some data may remain');
					ToastMessage.warning('Logged out, but some session data may remain');
					history.push('/scholar-login');
				}
			} else {
				// Handle non-200 response
				const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to logout. Please try again.';
				ToastMessage.error(errorMessage);
			}
		} catch (error) {
			// console.error('Logout error:', error);
			
			// Handle API errors using our consistent pattern
			const errorMessage = error?.messages?.error_messages?.[0] || 'Failed to connect to server. Logging out locally.';
			
			// Force logout on client side even if API fails
			clearUserSession();
			ToastMessage.warning(errorMessage);
			setTimeout(() => {
				history.push('/scholar-login');
			}, 1000);
		}
	};

	return (
		<div className="page-wrapper">
			<Layout className='page-body'>
				{/* Mobile Toggle Button and Menu */}
                <div className="mobile-menu-container">
                    <Button
                        type="primary"
                        className="mobile-sidebar-toggle"
                        icon={<MenuOutlined />}
                        onClick={toggleMobileSidebarVisibility}
                        aria-label="Toggle navigation menu"
                        shape="circle"
                        size="large"
                    />
                    
                    {/* Mobile Menu Popup */}
                    <div className={`mobile-menu-popup ${mobileSidebarVisible ? 'visible' : ''}`}>
                        {mobileMenuItems.map(item => (
                            <Button 
                                key={item.key}
                                className={`mobile-menu-item ${item.key === 'logout' ? 'active' : ''}`}
                                icon={item.icon}
                                onClick={item.onClick}
                                type="text"
                                block
                            >
                                {item.label}
                            </Button>
                        ))}
                    </div>
                </div>
                
                {/* Mobile Menu Overlay */}
                <div 
                    className={`mobile-menu-overlay ${mobileSidebarVisible ? 'visible' : ''}`}
                    onClick={() => setMobileSidebarVisible(false)}
                />
				
				<ScholarSidebar activePage="logout" />
				<Content className='main-content'>
					<div className="slo-content-wrapper">
						{/* Logout Confirmation */}
						<div className='slo-logout-container'>
							<Card className='slo-logout-box'>
								<LogoutOutlined className='slo-logout-icon' />
								<Title level={4} className='slo-logout-title'>Confirm Logout</Title>
								<Text className='slo-logout-message'>
									Are you sure you want to end your current session?
								</Text>
								<Space className='slo-logout-actions'>
									<Button
										size="large"
										onClick={handleCancel}
										className='slo-cancel-button'
									>
										Cancel
									</Button>
									<Button
										type="primary"
										size="large"
										onClick={handleLogout}
										className='slo-confirm-button'
									>
										Logout
									</Button>
								</Space>
							</Card>
						</div>

						{/* Company Branding */}
						<div className="slo-company-branding">
							<Text className="slo-company-text">©2025 MYT Softdev Solutions Inc.</Text>
						</div>
					</div>
				</Content>
			</Layout>
		</div>
	);
};

export default ScholarLogout; 